import {
  ServiceEventTicketPolicy,
  useCreateServiceEventTicketPolicyMutation,
  useDeleteServiceEventTicketPolicyMutation,
  useUpdateServiceEventTicketPolicyMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceEventTicketPolicyMutationProps = {}

export type UseServiceEventTicketPolicyMutationResult = {
  createServiceEventTicketPolicy: (assoc: Partial<ServiceEventTicketPolicy>, callback: EntityMutationCallback<ServiceEventTicketPolicy> | undefined) => void
  updateServiceEventTicketPolicy: (assoc: Partial<ServiceEventTicketPolicy>, callback: EntityMutationCallback<ServiceEventTicketPolicy> | undefined) => void
  deleteServiceEventTicketPolicy: (id: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceEventTicketPolicyMutation = (props: UseServiceEventTicketPolicyMutationProps = {}): UseServiceEventTicketPolicyMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceEventTicketPolicy, {isLoading: isCreating}] = useCreateServiceEventTicketPolicyMutation()
  const [_updateServiceEventTicketPolicy, {isLoading: isUpdating}] = useUpdateServiceEventTicketPolicyMutation()
  const [_deleteServiceEventTicketPolicy, {isLoading: isDeleting}] = useDeleteServiceEventTicketPolicyMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceEventTicketPolicyUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventTicketPolicyUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceEventTicketPolicyUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventTicketPolicyUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceEventTicketPolicyUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventTicketPolicyUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceEventTicketPolicy>, callback: EntityMutationCallback<ServiceEventTicketPolicy> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceEventTicketPolicy> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceEVentId: string, callback: EntityMutationCallback<ServiceEventTicketPolicy> | undefined) => {
    const promise = _deleteServiceEventTicketPolicy({id: serviceEVentId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceEventTicketPolicy, handleDeleteResult])

  const createServiceEventTicketPolicy = useCallback((serviceEVent: Partial<ServiceEventTicketPolicy>, callback: EntityMutationCallback<ServiceEventTicketPolicy> | undefined) => {
    const promise = _createServiceEventTicketPolicy(serviceEVent).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceEventTicketPolicy, handleCreateResult])

  const updateServiceEventTicketPolicy = useCallback((serviceEVent: Partial<ServiceEventTicketPolicy>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceEventTicketPolicy(serviceEVent).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceEventTicketPolicy, handleUpdateResult])

  const deleteServiceEventTicketPolicy = useCallback((serviceEVentId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.serviceeventticketpolicy", "Service Event Ticket Policy"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.serviceeventticketpolicy.confirm", "Are you sure you want to delete this service event ticket policy?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceEVentId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createServiceEventTicketPolicy: createServiceEventTicketPolicy,
    updateServiceEventTicketPolicy: updateServiceEventTicketPolicy,
    deleteServiceEventTicketPolicy: deleteServiceEventTicketPolicy,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
